import { collection, doc, getDoc, getDocs, setDoc, deleteDoc, Timestamp, writeBatch } from 'firebase/firestore'
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage'

import { LONG_LIVED_IMAGE_CACHE_CONTROL } from '../constants/storage.constants'
import { FirebaseCollections } from '../constants/firebase.constants'
import { getDb, getFirebaseStorage } from './firebase.service'
import { BeachPlanDocument, BeachPlanFirestoreData, BeachPlanSpotWithPlan, MAX_BEACH_PLANS } from '../types/beachPlan.types'
import { BeachAsset } from '../features/BeachPlan/jotai-modules/types'
import { DEFAULT_SPOT_ICON_MAX_WIDTHS } from '../features/BeachPlan/model/spotIconSizing'

const sortBeachPlans = (plans: BeachPlanFirestoreData[]): BeachPlanFirestoreData[] =>
  [...plans].sort((first, second) => first.displayOrder - second.displayOrder || first.name.localeCompare(second.name))

export const listBeachPlans = async (): Promise<BeachPlanFirestoreData[]> => {
  const db = await getDb()
  const snapshot = await getDocs(collection(db, FirebaseCollections.BEACH_PLANS))
  return sortBeachPlans(snapshot.docs.map((document) => ({ id: document.id, ...(document.data() as BeachPlanDocument) })))
}

export const listEnabledBeachPlans = async (): Promise<BeachPlanFirestoreData[]> => {
  const plans = await listBeachPlans()
  return plans.filter((plan) => plan.isEnabled)
}

export const getBeachPlan = async (beachPlanId: string): Promise<BeachPlanFirestoreData | null> => {
  const db = await getDb()
  const document = await getDoc(doc(db, FirebaseCollections.BEACH_PLANS, beachPlanId))
  if (!document.exists()) {
    return null
  }
  return { id: document.id, ...(document.data() as BeachPlanDocument) }
}

export const saveBeachPlan = async (beachPlanId: string, data: Partial<BeachPlanDocument>): Promise<void> => {
  const db = await getDb()
  await setDoc(
    doc(db, FirebaseCollections.BEACH_PLANS, beachPlanId),
    {
      ...data,
      updatedAt: Timestamp.now(),
    },
    { merge: true }
  )
}

export const reorderBeachPlans = async (orderedIds: string[]): Promise<void> => {
  const db = await getDb()
  const batch = writeBatch(db)
  const updatedAt = Timestamp.now()

  orderedIds.forEach((beachPlanId, index) => {
    batch.set(
      doc(db, FirebaseCollections.BEACH_PLANS, beachPlanId),
      {
        displayOrder: index,
        isDefault: index === 0,
        updatedAt,
      },
      { merge: true }
    )
  })

  await batch.commit()
}

export const createBeachPlan = async (data: Omit<BeachPlanDocument, 'updatedAt'>): Promise<string> => {
  const db = await getDb()
  const plans = await listBeachPlans()
  if (plans.length >= MAX_BEACH_PLANS) {
    throw new Error(`Maximum of ${MAX_BEACH_PLANS} beach plans allowed`)
  }

  const docRef = doc(collection(db, FirebaseCollections.BEACH_PLANS))
  await setDoc(docRef, {
    ...data,
    updatedAt: Timestamp.now(),
  })
  return docRef.id
}

export const deleteBeachPlan = async (beachPlanId: string): Promise<void> => {
  const db = await getDb()
  await deleteDoc(doc(db, FirebaseCollections.BEACH_PLANS, beachPlanId))
}

export const getAllSpotsWithPlanMetadata = async (): Promise<BeachPlanSpotWithPlan[]> => {
  const plans = await listBeachPlans()
  return plans.flatMap((plan) =>
    (plan.spots || []).map((spot) => ({
      ...spot,
      beachPlanId: plan.id,
      beachPlanName: plan.name,
    }))
  )
}

export const uploadBeachPlanBackground = async (beachPlanId: string, file: File): Promise<string> => {
  const storage = await getFirebaseStorage()
  const fileExtension = file.name.split('.').pop()
  const fileNameWithoutExtension = file.name.replace(`.${fileExtension}`, '')
  const uniqueFileName = `${fileNameWithoutExtension}-${Date.now()}.${fileExtension}`
  const storageRef = ref(storage, `configurations/beachPlan/${beachPlanId}/${uniqueFileName}`)
  await uploadBytes(storageRef, file, { cacheControl: LONG_LIVED_IMAGE_CACHE_CONTROL })
  return await getDownloadURL(storageRef)
}

export const allocateUniqueSpotId = (allSpots: BeachAsset[]): number => {
  const usedIds = new Set(allSpots.map((spot) => spot.id).filter((id): id is number => id != null))
  let candidate = Date.now()
  while (usedIds.has(candidate)) {
    candidate += 1
  }
  return candidate
}

export const createDefaultBeachPlanDocument = (name = 'Main'): Omit<BeachPlanDocument, 'updatedAt'> => ({
  name,
  displayOrder: 0,
  isDefault: true,
  isEnabled: true,
  rows: 0,
  columns: 0,
  background: '',
  spots: [],
  spotIconMaxWidths: DEFAULT_SPOT_ICON_MAX_WIDTHS,
})

export const assertNoDuplicateSpotIdsAcrossPlans = (plans: BeachPlanFirestoreData[], planId: string, spots: BeachAsset[]): void => {
  const otherSpots = plans.filter((plan) => plan.id !== planId).flatMap((plan) => plan.spots || [])
  const otherIds = new Set(otherSpots.map((spot) => spot.id).filter((id): id is number => id != null))

  spots.forEach((spot) => {
    if (spot.id != null && otherIds.has(spot.id)) {
      throw new Error(`Spot id ${spot.id} already exists on another beach plan`)
    }
  })
}
